import LegacyChart from "interface/LegacyChart";
import ProcessedChartData from "interface/ProcessedChartData";
//import ZERONE_COLORS from "constants/data/global/ZeroneColors";
import { getSeriesColor } from "constants/data/global/SeriesColors";

interface SeriesConfig {
    dataKey: string;
    name: string;
    axis: 'left' | 'right';
    unit: string;
    color: string;
    type: 'line' | 'bar' | 'area' | 'scatter' | null;
}

interface ProcessedChartDataResult {
    processedData: ProcessedChartData[];
    hasLeftAxis: boolean;
    hasRightAxis: boolean;
    seriesConfig: SeriesConfig[];
    formatAxisLabel: (axis: 'left' | 'right') => string;
    formatTooltipValue: (value: any, name: string, props: any) => [string, string];
}

/**
 * Processes chart data from a LegacyChart into a format suitable for rendering in a bar chart.
 * 
 * @param {LegacyChart} chart - The chart data to process.
 * @returns {ProcessedChartDataResult} - The processed data and configuration for the chart.
 * 
 * @example
 * const chart: LegacyChart = {
 *   title: "Sample Chart",
 *   type: "bar",
 *   xAxisLabel: "Categories",
 *   yAxisLabel: "Values",
 *   annotation: {
 *     title: "Important Note",
 *     value: "This is a critical point"
 *   },
 *   series: [
 *     {
 *       name: "Series 1",
 *       data: [
 *         { x: "January", yValue: 10 },
 *         { x: "February", yValue: 20 },
 *         { x: "March", yValue: 30 }
 *       ]
 *     }
 *   ]
 * }
 * 
 * const processedData = processChartData(chart);
 * 
 * // Example output:
 * {
 *   processedData: [
 *     { x: "January", series_0: 10 },
 *     { x: "February", series_0: 20 },
 *     { x: "March", series_0: 30 }
 *   ],
 *   hasLeftAxis: true,
 *   hasRightAxis: false,
 *   seriesConfig: [
 *     {
 *       dataKey: "series_0",
 *       name: "Series 1",
 *       axis: "left",
 *       unit: "",
 *       color: "#007bff" // ZERONE_COLORS.primary
 *     }
 *   ],
 *   formatAxisLabel: (axis) => {
 *     return axis === 'left' ? 'Left Axis Unit' : 'Right Axis Unit';
 *   },
 *   formatTooltipValue: (value, name) => {
 *     return [`${value} unit`, name];
 *   }
 * }
 * 
 */
const processChartData = (chart: LegacyChart): ProcessedChartDataResult => {
    const dataMap = new Map<string, ProcessedChartData>();

    chart.series.forEach((series, index) => {
        series.data.forEach((dataPoint) => {
            if (!dataMap.has(dataPoint.x)) {
                dataMap.set(dataPoint.x, { x: dataPoint.x });
            }
            const existingData = dataMap.get(dataPoint.x);
            if (existingData) {
                existingData[`series_${index}`] = dataPoint.yValue;
            }
        })
    })

    const processedData = Array.from(dataMap.values());

    const hasLeftAxis = chart.series.some(series => series.axis === 'left');
    const hasRightAxis = chart.series.some(series => series.axis === 'right');

    const seriesConfig: SeriesConfig[] = chart.series.map((series, index) => ({
        dataKey: `series_${index}`,
        name: series.label,
        axis: series.axis ?? 'left',
        unit: series.unit ?? '',
        type: series.type ?? "bar",
        color: getSeriesColor(index),
    }));

    // const seriesConfig: SeriesConfig[] = chart.series.map((series, index) => ({
    //     dataKey: `series_${index}`,
    //     name: series.label,
    //     axis: series.axis ?? 'left',
    //     unit: series.unit ?? '',
    //     type: series.type ?? "bar",
    //     color: index === 0 ? ZERONE_COLORS.primary : ZERONE_COLORS.secondary,
    // }));

    const formatAxisLabel = (axis: 'left' | 'right'): string => {
        const axisUnits = chart.series
            .filter(series => series.axis === axis)
            .map(series => series.unit)
            .filter((unit, index, self) => self.indexOf(unit) === index);
        
        return axisUnits.length > 0 ? axisUnits.join(', ') : '';
    }

    const formatTooltipValue = (value: any, name: string, props: any): [string, string] => {
        const series = seriesConfig.find(s => s.dataKey === props.dataKey);
        const formattedValue = Number(value).toLocaleString();

        const valueText = series?.unit && !series.name.toLowerCase().includes(series.unit.toLowerCase())
            ? `${formattedValue} ${series.unit} `
            : `${formattedValue} `;

        return [valueText, series?.name || name];
    };

    return {
        processedData,
        hasLeftAxis,
        hasRightAxis,
        seriesConfig,
        formatAxisLabel: formatAxisLabel,
        formatTooltipValue: formatTooltipValue
    };
}

export default processChartData;
